/**
 * Created by JUNO PENG on 1/5/2024.
 * Description:
 * AdminPortfolioUpload Component
 * ------ maintenance history ------
 */
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, HostListener } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { ColDef, GridApi, GridOptions } from 'ag-grid-community';
import { Subscription } from 'rxjs';
import { AppState } from '../../../../redux';
import { filter, take } from 'rxjs/operators';
import { DEFAULT_PAGE_SIZE } from '../../grid-utility';
import { ResearchView } from '../../../../constants/business.constants';
import { NotificationActionTypes } from '../../../../redux/actions/notification.actions';
import { PortfolioUploadModel } from '../../../../tamalelibs/models/portfolio-upload.model';
import { StringLiteralsPipe } from '../../../../pipes/translate.pipe';
import { SlideSheetService } from '../../../../services/slide-sheet.service';
import { SlideSheetActionTypes } from '../../../slide-sheet/slide-sheet.model';
import { ToastService } from '../../../../widgets/toast/toast.service';
import { NotificationStyles } from '../../../../widgets/notification/notification.model';
import { AdminPortfolioUploadButtonComponent } from './admin-portfolio-upload-button.componment';
import { AdminPortfolioUploadService, PortfolioUpload, PortfolioUploadType } from './admin-portfolio-upload.service';
import { PortfolioActionTypes } from '../../../../redux/actions/portfolio.action';
import { portfolioUploadSelector } from '../../../../redux/reducers/portfolio.reducer';
import { lastCustomActionSelector } from '../../../../redux/reducers/action-history.reducer';
import { SystemActionTypes } from '../../../../tamalelibs/redux/actions/system.actions';
@Component({
    selector: 'tam-admin-portfolio-upload',
    templateUrl: './admin-portfolio-upload.component.html',
    styleUrls: ['./admin-portfolio-upload.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminPortfolioUploadComponent implements OnInit, OnDestroy {
    /**
    * an array of column definitions.
    */
    columnDefs: Array<ColDef> = [];

    /**
     * The option settings for the inner grid.
     */
    gridOptions: GridOptions;

    /**
     * for spinner
     */
    loading = true;

    /**
     * new portfolio button
     */
    protfolioUpload = StringLiteralsPipe.translate('portfolio.create_new_protfolio_upload_configuration');

    /**
     * default to hide the no data indicator.
     */
    noDataStyle = 'none';

    /**
     * the data source of the grid.
     */
    rowData: Array<PortfolioUploadModel> = [];

    /**
     * the Grid API reference
     */
    private _gridApi: GridApi;
    private _columnApi;

    /**
     * the subscription collection persisting for unsubscribing them on destroying
     */
    private _destroySubscriptions: Array<Subscription> = [];

    constructor(
        private _store: Store<AppState>,
        private _adminPortfolioUploadService: AdminPortfolioUploadService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _slideSheetService: SlideSheetService,
        private _toast: ToastService) { }

    ngOnInit() {

        this._store.dispatch({
            type: SystemActionTypes.GET_CONTACTS
        });

        // to be used by edit portfolio page
        this._destroySubscriptions.push(
            this._store.pipe(
                select(portfolioUploadSelector),
                filter(res => res),
            ).subscribe(res => {
                if (res.type === PortfolioActionTypes.GETPORTFOLIOS_SUCCESS) {
                    this.rowData = res.payload;
                    // Due to when switch router,grid header was loss.reload grid config can fix this issue.
                    this._loadGridConfig();
                    this._changeDetectorRef.detectChanges();
                }
                this.loading = false;
            }),
            this._store.pipe(
                select(lastCustomActionSelector)
            ).subscribe(action => {
                this._onSubscribeActionMessage(action);
            }),
            this._adminPortfolioUploadService.subject$.subscribe((action) => {
                if (action.type === PortfolioUploadType.DELETE_PORTFOLIO_UPLOAD) {
                    this._removeItem(action.payload.configID);
                    this._changeDetectorRef.detectChanges();
                }
            }),
        );
        this._store.dispatch({
            type: PortfolioActionTypes.GETPORTFOLIOS
        });
        this._loadGridConfig();
    }

    ngOnDestroy() {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
    }

    @HostListener('window:resize', ['$event'])
    onResize(e) {
        if (!this._gridApi) {
            return;
        }
        this._gridApi.sizeColumnsToFit();
    }

    /** Triggered when create button is clicked; opens editor menu */
    onButtonClick() {
        this._slideSheetService.slideSheetActionSubject$.next({
            type: SlideSheetActionTypes.ADMIN_CONSOLE_PORTFOLIO_UPLOAD_EDITOR
        });
    }

    onDisplayedColumnsChanged(event) {
        event.api.sizeColumnsToFit();
    }

    onColumnEverythingChanged(event) {
        if (event.source === 'contextMenu') {
            const sort = [
                {
                    colId: 'name',
                    sort: 'asc'
                }
            ];
            this._columnApi.setRowGroupColumns([]);
            this._gridApi.setFilterModel(null);
            this._columnApi.applyColumnState({ state: sort });
            this._changeDetectorRef.detectChanges();
        }
    }

    onGridReady(params) {
        this._gridApi = params.api;
        this._columnApi = params.columnApi;
    }

    private _compareValue(columnDefs) {
        columnDefs.forEach(def => {
            def.comparator = function (valueA, valueB, nodeA, nodeB) {
                if (valueA && valueB && valueA.length > 0 && valueB.length > 0) {
                    return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
                }
            };
        });
    }

    private _generateColDefs(): Array<ColDef> {
        const cellStyleHandler = () => {
            return {
                'white-space': 'nowrap',
                'overflow': 'hidden',
                'text-overflow': 'ellipsis',
                'display': 'block',
                'border': 'none'
            };
        };
        return [
            {
                headerName: 'Config Name',
                colId: 'configName',
                valueGetter: 'data.configName',
                tooltipValueGetter: this._tooltip,
                sortable: true,
                minWidth: 50,
                width: 300,
                sort: 'asc'
            },
            {
                headerName: 'Flat Filename',
                colId: 'portfolioFileName',
                valueGetter: 'data.portfolioFileName',
                tooltipValueGetter: this._tooltip,
                sortable: true,
                minWidth: 50,
                width: 300,
            },
            {
                headerName: 'Target Portfolio/Entity',
                colId: 'entityMatching',
                valueGetter: 'data.targetEntity.shortName',
                tooltipValueGetter: this._tooltip,
                minWidth: 50,
                maxWidth: 300,
                sortable: true,
            },

            {
                cellStyle: (params) => {
                    return Object.assign({}, cellStyleHandler(), {
                        'padding': '16px 0 0 0',
                        'margin': 0,
                        'height': '100%',
                        'text-align': 'center'
                    });
                },
                headerName: 'Edit/Delete',
                minWidth: 96,
                width: 96,
                maxWidth: 96,
                sortable: false,
                suppressColumnsToolPanel: true,
                suppressMovable: true,
                menuTabs: [],
                headerClass: 'header-show',
                cellRenderer: AdminPortfolioUploadButtonComponent,
            }
        ];
    }

    private _generateGridOpions(): GridOptions {
        return {
            defaultColDef: {
                sortable: true,
                resizable: true,
                filter: 'agSetColumnFilter',
                menuTabs: ['filterMenuTab', 'columnsMenuTab', 'generalMenuTab'],
            },
            cacheBlockSize: ResearchView.cacheBlockSize,
            suppressCellFocus: true,
            suppressColumnVirtualisation: true,
            enableBrowserTooltips: true,
            colResizeDefault: 'shift',
            rowSelection: 'single',
            sortingOrder: ['desc', 'asc'],
            maintainColumnOrder: true,
            suppressContextMenu: true,
            suppressDragLeaveHidesColumns: true,
            suppressRowGroupHidesColumns:true,
            suppressMakeColumnVisibleAfterUnGroup: true,
            suppressRowTransform: true,
            getRowHeight: () => 40,
        };
    }

    private _loadGridConfig() {
        this.columnDefs = this._generateColDefs();
        this._compareValue(this.columnDefs);
        this.gridOptions = this._generateGridOpions();
    }

    private _onSubscribeActionMessage(action) {
        if (action.type === PortfolioActionTypes.CREATEPORTFOLIOS_SUCCESS) {
            this._store.dispatch({
                type: PortfolioActionTypes.GETPORTFOLIOS
            });
            this._toast.notify({
                style: NotificationStyles.Success,
                message: action.payload.successMessage
            });
        }
        if (action.type === PortfolioActionTypes.EDITPORTFOLIOS_SUCCESS) {
            this._store.dispatch({
                type: PortfolioActionTypes.GETPORTFOLIOS
            });
            this._toast.notify({
                style: NotificationStyles.Success,
                message: action.payload.successMessage
            });
        }
        if ((action.type === PortfolioActionTypes.EDITPORTFOLIOS_FAILED) || (action.type === PortfolioActionTypes.CREATEPORTFOLIOS_FAILED) || (action.type === PortfolioActionTypes.GETPORTFOLIOS_FAILED)) {
            this._toast.notify({
                style: NotificationStyles.Error,
                message: action.payload
            });
        }
    }

    private _removeItem(id: string) {
        const index = this.rowData.indexOf(this.rowData.find(data => (data.configID === id)));
        if (index > -1) {
            this.rowData.splice(index, 1);

            if (this._gridApi) {
                this._gridApi.setRowData(this.rowData);
                this._gridApi.sizeColumnsToFit();
            }
        }
    }

    private _tooltip(params) {
        return (params.value);
    }
}
